import * as THREE from 'three';
import type { TargetClass } from '../../sim/targetTypes';
import type { Vec3 } from '../../sim/mathx';
import type { InterpolatedTelemetry } from '../../state/interpolate';
import type { TelemetrySnapshot } from '../../state/telemetryBuffer';
import { createHomeMarker, disposeHomeMarker, RadarSiteView } from './RadarSiteView';
import { ScoutView } from './ScoutView';
import { TargetView } from './TargetView';

export const TARGET_VIEW_POOL_CAP = 8;

export interface EntityLayerOptions {
  radarEnu: Vec3;
  homeEnu: Vec3;
  classById?: ReadonlyMap<string, TargetClass>;
}

type InterpolatedTarget = InterpolatedTelemetry['targets'][number];

/** Owns persistent entity views and diffs interpolated target ids each frame. */
export class EntityLayer {
  readonly scout = new ScoutView();
  readonly radar: RadarSiteView;
  readonly homeMarker: THREE.Group;

  private readonly scene: THREE.Scene;
  private readonly classById: ReadonlyMap<string, TargetClass>;
  private readonly activeTargets = new Map<string, TargetView>();
  private readonly pooledTargets: TargetView[] = [];
  private disposed = false;

  constructor(scene: THREE.Scene, options: EntityLayerOptions) {
    this.scene = scene;
    this.classById = options.classById ?? new Map<string, TargetClass>();
    this.radar = new RadarSiteView(options.radarEnu);
    this.homeMarker = createHomeMarker(options.homeEnu);
    scene.add(this.scout, this.radar, this.homeMarker);
  }

  get activeTargetCount(): number {
    return this.activeTargets.size;
  }

  get pooledCount(): number {
    return this.pooledTargets.length;
  }

  getTargetView(id: string): TargetView | undefined {
    return this.activeTargets.get(id);
  }

  syncTargets(interpTargets: readonly InterpolatedTarget[]): void {
    if (this.disposed) return;
    const incoming = new Map(interpTargets.map((target) => [target.id, target]));

    for (const [id, view] of this.activeTargets) {
      if (incoming.has(id)) continue;
      this.activeTargets.delete(id);
      view.prepareForPool();
      if (this.pooledTargets.length < TARGET_VIEW_POOL_CAP) this.pooledTargets.push(view);
      else view.dispose();
    }

    for (const [id, target] of incoming) {
      let view = this.activeTargets.get(id);
      if (!view) {
        const targetClass = this.classById.get(id) ?? 'drone';
        view = this.acquireTarget(id, targetClass);
        this.activeTargets.set(id, view);
        this.scene.add(view);
      }
      view.setPosition(target.posEnu);
    }
  }

  update(interp: InterpolatedTelemetry, telem: TelemetrySnapshot, dtSeconds: number): void {
    if (this.disposed) return;
    this.scout.update(interp.scout, telem.scout, dtSeconds);
    this.radar.update(dtSeconds);
    this.syncTargets(interp.targets);
    for (const target of interp.targets) {
      this.activeTargets.get(target.id)?.update(target.posEnu, interp.time);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const view of this.activeTargets.values()) view.dispose();
    for (const view of this.pooledTargets) view.dispose();
    this.activeTargets.clear();
    this.pooledTargets.length = 0;
    this.scout.dispose();
    this.radar.dispose();
    disposeHomeMarker(this.homeMarker);
  }

  private acquireTarget(id: string, targetClass: TargetClass): TargetView {
    const compatibleIndex = this.pooledTargets.findIndex(
      (view) => view.targetClass === targetClass,
    );
    if (compatibleIndex < 0) return new TargetView(id, targetClass);
    const [view] = this.pooledTargets.splice(compatibleIndex, 1);
    view!.reuse(id);
    return view!;
  }
}
